import React, { useMemo, useState } from 'react';
import Icon from '../components/Icon';
import KpiCard from '../components/KpiCard';
import Toggle from '../components/Toggle';
import {
  maintServices as initialSvc,
  maintRequests as initialReq,
  MaintSvc,
  MaintRequest,
} from '../data/mock';

type Tab = 'requests' | 'catalog';
type ReqFilter = 'all' | MaintRequest['status'];
const REQ_FILTERS: ReqFilter[] = ['all', 'new', 'assigned', 'quoted', 'accepted', 'in-progress', 'completed'];

const urgencyClass = (u: MaintRequest['urgency']): string => {
  if (u === 'urgent') return 'bg-[#FEE2E2] text-[#DC2626]';
  if (u === 'normal') return 'bg-[#FEF3C7] text-[#D97706]';
  return 'bg-[#DBEAFE] text-[#2563EB]';
};

const statusBadge = (s: MaintRequest['status']): string => {
  const map: Record<MaintRequest['status'], string> = {
    'new':         'badge-new',
    'assigned':    'badge-contacted',
    'quoted':      'badge-pending',
    'accepted':    'badge-converted',
    'in-progress': 'badge-pending',
    'completed':   'badge-active',
  };
  return `badge ${map[s]}`;
};

const Maintenance: React.FC = () => {
  const [tab, setTab] = useState<Tab>('requests');
  const [services, setServices] = useState<MaintSvc[]>(initialSvc);
  const [requests, setRequests] = useState<MaintRequest[]>(initialReq);
  const [reqFilter, setReqFilter] = useState<ReqFilter>('all');
  const [catFilter, setCatFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<number | null>(null);

  const cats = useMemo<string[]>(() => {
    const set = new Set<string>(['all']);
    services.forEach((s) => set.add(s.cat));
    return Array.from(set);
  }, [services]);

  const filteredSvc = useMemo<MaintSvc[]>(() => {
    return catFilter === 'all' ? services : services.filter((s) => s.cat === catFilter);
  }, [services, catFilter]);

  const filteredReq = useMemo<MaintRequest[]>(() => {
    return reqFilter === 'all'
      ? requests
      : requests.filter((r) => r.status === reqFilter);
  }, [requests, reqFilter]);

  const newCt = requests.filter((r) => r.status === 'new').length;
  const assigned = requests.filter((r) => r.status === 'assigned').length;
  const quoted = requests.filter((r) => r.status === 'quoted').length;
  const completed = requests.filter((r) => r.status === 'completed').length;
  const activeCt = requests.filter((r) => r.status !== 'completed').length;

  const toggleSvc = (id: number) =>
    setServices((prev) =>
      prev.map((s) => (s.id === id ? { ...s, active: !s.active } : s))
    );

  const updReqStatus = (id: number, status: MaintRequest['status']) =>
    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status } : r))
    );

  return (
    <div>
      {/* Tabs */}
      <div
        className="flex mb-5"
        style={{ borderBottom: '2px solid var(--g100)' }}
      >
        <button
          onClick={() => setTab('requests')}
          className="flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all"
          style={{
            color: tab === 'requests' ? 'var(--brand)' : 'var(--g500)',
            borderBottom: `2px solid ${tab === 'requests' ? 'var(--brand)' : 'transparent'}`,
            marginBottom: -2,
            background: 'none',
            border: 'none',
            borderBottomWidth: 2,
            borderBottomStyle: 'solid',
            borderBottomColor: tab === 'requests' ? 'var(--brand)' : 'transparent',
            cursor: 'pointer',
          }}
        >
          <Icon name="message-square" size={16} color={tab === 'requests' ? 'var(--brand)' : 'var(--g500)'} />
          Requests
          <span
            className="text-[11px] font-bold rounded-full px-2 py-0.5"
            style={{
              background: tab === 'requests' ? 'var(--brand-bg)' : 'var(--g100)',
              color: tab === 'requests' ? 'var(--brand)' : 'var(--g600)',
              minWidth: 20,
              textAlign: 'center',
            }}
          >
            {activeCt}
          </span>
        </button>
        <button
          onClick={() => setTab('catalog')}
          className="flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all"
          style={{
            color: tab === 'catalog' ? 'var(--brand)' : 'var(--g500)',
            marginBottom: -2,
            background: 'none',
            border: 'none',
            borderBottomWidth: 2,
            borderBottomStyle: 'solid',
            borderBottomColor: tab === 'catalog' ? 'var(--brand)' : 'transparent',
            cursor: 'pointer',
          }}
        >
          <Icon name="wrench" size={16} color={tab === 'catalog' ? 'var(--brand)' : 'var(--g500)'} />
          Service Catalog
        </button>
      </div>

      {tab === 'catalog' ? (
        <>
          <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
            <div className="flex gap-2 flex-wrap">
              {cats.map((c) => {
                const active = catFilter === c;
                return (
                  <button
                    key={c}
                    onClick={() => setCatFilter(c)}
                    className="capitalize"
                    style={{
                      padding: '8px 16px',
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                      background: active ? 'var(--brand-light)' : '#fff',
                      color: active ? 'var(--brand)' : 'var(--g600)',
                    }}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <button className="btn btn-primary">
              <Icon name="plus" size={16} color="#fff" /> Add Service
            </button>
          </div>

          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            }}
          >
            {filteredSvc.map((s, i) => (
              <div
                key={s.id}
                className="card flex flex-col gap-3 p-5 animate-fadeIn"
                style={{
                  opacity: s.active ? 1 : 0.55,
                  animationDelay: `${i * 50}ms`,
                }}
              >
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div
                      className="text-[11px] font-bold uppercase tracking-wider text-brand mb-1"
                    >
                      {s.cat}
                    </div>
                    <div className="text-base font-bold mb-1">{s.name}</div>
                    <div className="text-[13px] text-g500 leading-relaxed">
                      {s.desc}
                    </div>
                  </div>
                  <Toggle on={s.active} onChange={() => toggleSvc(s.id)} />
                </div>
                <div
                  className="flex justify-between items-center pt-2"
                  style={{ borderTop: '1px solid var(--g100)' }}
                >
                  <span className="text-lg font-bold text-brand">{s.price}</span>
                  <button className="btn btn-secondary btn-sm">
                    <Icon name="pencil" size={14} /> Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex gap-4 flex-wrap mb-6">
            <KpiCard
              icon="message-square"
              label="New Requests"
              value={String(newCt)}
              change={newCt > 0 ? `${newCt} pending` : 'None'}
              up={newCt === 0}
              delay={0}
            />
            <KpiCard
              icon="user-check"
              label="Assigned"
              value={String(assigned)}
              change="Awaiting quote"
              up
              delay={80}
            />
            <KpiCard
              icon="dollar-sign"
              label="Quoted"
              value={String(quoted)}
              change="Awaiting approval"
              up
              delay={160}
            />
            <KpiCard
              icon="check-circle"
              label="Completed"
              value={String(completed)}
              change="All time"
              up
              delay={240}
            />
          </div>

          <div className="flex justify-between items-center flex-wrap gap-3 mb-5">
            <div className="flex gap-2 flex-wrap">
              {REQ_FILTERS.map((f) => {
                const active = reqFilter === f;
                return (
                  <button
                    key={f}
                    onClick={() => setReqFilter(f)}
                    className="capitalize"
                    style={{
                      padding: '8px 16px',
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                      background: active ? 'var(--brand-light)' : '#fff',
                      color: active ? 'var(--brand)' : 'var(--g600)',
                    }}
                  >
                    {f}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            className="bg-white rounded-card overflow-hidden border border-g100"
            style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
          >
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Customer','Property','Category','Urgency','Provider','Quote','Status','Details'].map((h, i) => (
                    <th
                      key={h}
                      className={
                        'px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap' +
                        (i === 7 ? ' text-right' : '')
                      }
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredReq.map((r) => {
                  const isOpen = expanded === r.id;
                  return (
                    <React.Fragment key={r.id}>
                      <tr
                        className="cursor-pointer"
                        style={{ background: isOpen ? 'var(--g50)' : undefined }}
                        onClick={() => setExpanded(isOpen ? null : r.id)}
                      >
                        <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                          {r.customer}
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          <span className="flex items-center gap-2">
                            <Icon name="map-pin" size={14} color="var(--g400)" />
                            {r.property.split(',')[0]}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          <span className="type-badge type-rental">{r.cat}</span>
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          <span
                            className={
                              'inline-flex items-center text-[11px] font-bold px-2 py-0.5 rounded capitalize ' +
                              urgencyClass(r.urgency)
                            }
                          >
                            {r.urgency}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          {r.provider ? (
                            <span className="font-semibold">{r.provider}</span>
                          ) : (
                            <span className="text-g500">Unassigned</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          {r.quote ? (
                            <span className="font-semibold" style={{ color: 'var(--green)' }}>
                              {r.quote}
                            </span>
                          ) : (
                            <span className="text-g500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                          <span className={statusBadge(r.status)}>
                            {r.status.replace('-', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                        </td>
                        <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100 text-right">
                          <span
                            style={{
                              display: 'inline-block',
                              transition: 'transform .2s',
                              transform: `rotate(${isOpen ? 180 : 0}deg)`,
                            }}
                          >
                            <Icon name="chevron-down" size={18} color="var(--g400)" />
                          </span>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0 }}>
                            <div
                              className="flex gap-5 px-6 py-5 flex-wrap"
                              style={{ background: 'var(--g50)' }}
                            >
                              <div className="flex-1 min-w-[200px]">
                                <div className="text-[13px] text-g600 leading-loose">
                                  <strong>Customer:</strong> {r.customer}
                                  <br />
                                  <Icon name="mail" size={12} color="var(--g400)" /> {r.email}
                                  <br />
                                  <Icon name="phone" size={12} color="var(--g400)" /> {r.phone}
                                  <br />
                                  <strong>Submitted:</strong> {r.submitted}
                                </div>
                                <div
                                  className="text-[13px] text-g700 leading-relaxed bg-white border border-g200 rounded-[10px] px-4 py-3 mt-2"
                                >
                                  <Icon name="package" size={14} color="var(--g400)" /> {r.issue}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 min-w-[160px]">
                                {r.status === 'new' && (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updReqStatus(r.id, 'assigned');
                                    }}
                                  >
                                    Assign Provider
                                  </button>
                                )}
                                {r.status === 'assigned' && (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updReqStatus(r.id, 'quoted');
                                    }}
                                  >
                                    Send Quote
                                  </button>
                                )}
                                {r.status === 'quoted' && (
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updReqStatus(r.id, 'accepted');
                                    }}
                                  >
                                    Mark Accepted
                                  </button>
                                )}
                                {r.status === 'accepted' && (
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updReqStatus(r.id, 'in-progress');
                                    }}
                                  >
                                    Start Work
                                  </button>
                                )}
                                {r.status === 'in-progress' && (
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      updReqStatus(r.id, 'completed');
                                    }}
                                  >
                                    Mark Complete
                                  </button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default Maintenance;
